import { useEffect, useState } from "react";
import { Mail, UserPlus, Trash2, RefreshCw, Download } from "lucide-react";
import {
  getAllSubscribers,
  getActiveSubscribersCount,
  addSubscriberManually,
  deleteSubscriber,
  reactivateSubscriber,
  unsubscribeFromNewsletter,
} from "../services/newsletter.service";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";

export default function Newsletter() {
  const [subscribers, setSubscribers] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filter, setFilter] = useState("all"); // all, active, inactive

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    const [subs, count] = await Promise.all([
      getAllSubscribers(),
      getActiveSubscribersCount(),
    ]);
    setSubscribers(subs);
    setActiveCount(count);
    setLoading(false);
  }

  const handleAddSubscriber = async () => {
    if (!newEmail.trim()) {
      toast.error("Please enter a valid email");
      return;
    }

    setIsSubmitting(true);

    const result = await addSubscriberManually(newEmail.trim());

    if (result.success) {
      toast.success(result.message);
      setNewEmail("");
      setShowAddModal(false);
      loadData();
    } else {
      toast.error(result.message);
    }

    setIsSubmitting(false);
  };

  const handleDelete = async (id, email) => {
    if (!window.confirm(`Are you sure you want to delete ${email}?`)) {
      return;
    }

    const result = await deleteSubscriber(id);

    if (result.success) {
      toast.success(result.message);
      loadData();
    } else {
      toast.error(result.message);
    }
  };

  const handleToggleStatus = async (subscriber) => {
    const result = subscriber.is_active
      ? await unsubscribeFromNewsletter(subscriber.id)
      : await reactivateSubscriber(subscriber.id);

    if (result.success) {
      toast.success(result.message);
      loadData();
    } else {
      toast.error(result.message);
    }
  };

  const handleExport = () => {
    const filteredData = getFilteredSubscribers();

    const exportData = filteredData.map((sub) => ({
      Email: sub.email,
      Status: sub.is_active ? "Active" : "Inactive",
      "Subscribed At": new Date(sub.subscribed_at).toLocaleString(),
      "Unsubscribed At": sub.unsubscribed_at
        ? new Date(sub.unsubscribed_at).toLocaleString()
        : "N/A",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Subscribers");

    const filterName = filter === "all" ? "All" : filter === "active" ? "Active" : "Inactive";
    XLSX.writeFile(wb, `Newsletter_Subscribers_${filterName}_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Exported successfully!");
  };

  const getFilteredSubscribers = () => {
    if (filter === "active") return subscribers.filter((s) => s.is_active);
    if (filter === "inactive") return subscribers.filter((s) => !s.is_active);
    return subscribers;
  };

  const filteredSubscribers = getFilteredSubscribers();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 flex items-start gap-4">
        <div className="flex-shrink-0 mt-0.5">
          <Mail className="w-6 h-6 text-blue-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-bold text-slate-900 mb-2">Newsletter Management</h3>
          <p className="text-sm text-slate-700 leading-relaxed">
            Manage your newsletter subscribers. View all subscribers, their status, and export the list.
            Active subscribers will receive your newsletters. You can manually add, activate, deactivate, or delete subscribers.
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <StatCard title="Total Subscribers" value={subscribers.length} />
        <StatCard title="Active Subscribers" value={activeCount} success />
        <StatCard
          title="Inactive Subscribers"
          value={subscribers.length - activeCount}
          muted
        />
      </div>

      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-4 rounded-lg shadow-sm">
        <div className="flex gap-2">
          <FilterButton
            active={filter === "all"}
            onClick={() => setFilter("all")}
            label="All"
          />
          <FilterButton
            active={filter === "active"}
            onClick={() => setFilter("active")}
            label="Active"
          />
          <FilterButton
            active={filter === "inactive"}
            onClick={() => setFilter("inactive")}
            label="Inactive"
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
          >
            <Download size={18} />
            Export to Excel
          </button>

          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 rounded-lg hover:bg-amber-600 transition-colors text-sm font-bold"
          >
            <UserPlus size={18} />
            Add Subscriber
          </button>
        </div>
      </div>

      {/* Subscribers Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Subscribed At
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredSubscribers.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center text-slate-500">
                    No subscribers found
                  </td>
                </tr>
              ) : (
                filteredSubscribers.map((subscriber) => (
                  <tr key={subscriber.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Mail size={16} className="text-slate-400" />
                        <span className="text-sm text-slate-900">{subscriber.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {subscriber.is_active ? (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-slate-100 text-slate-600">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {new Date(subscriber.subscribed_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleStatus(subscriber)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title={subscriber.is_active ? "Deactivate" : "Reactivate"}
                        >
                          <RefreshCw size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(subscriber.id, subscriber.email)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Subscriber Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Add Subscriber</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="subscriber@example.com"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  disabled={isSubmitting}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setNewEmail("");
                  }}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddSubscriber}
                  className="flex-1 px-4 py-2 bg-amber-500 text-slate-900 rounded-lg hover:bg-amber-600 transition-colors font-bold disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Adding..." : "Add Subscriber"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Helper Components */
function StatCard({ title, value, success, muted }) {
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <p className="text-sm text-gray-500">{title}</p>
      <p
        className={`text-3xl font-bold mt-2 ${
          success
            ? "text-green-600"
            : muted
            ? "text-gray-400"
            : "text-slate-900"
        }`}
      >
        {value ?? "—"}
      </p>
    </div>
  );
}

function FilterButton({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
        active
          ? "bg-amber-500 text-slate-900 shadow"
          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
      }`}
    >
      {label}
    </button>
  );
}
